/**
 * PTW Template Library — Data Foundation (Task #2484)
 *
 * Defines the `ptw_library_entries` table that backs the pull-based template
 * library tenants browse to get a disconnected starting-point copy. This is
 * complementary to the push-based Blueprint cascade (Task #1356) — the two
 * are deliberately separate. A library entry can later be promoted to a
 * Blueprint by a Super Admin, but that is out of scope here.
 *
 * Do NOT edit existing shared schema files to add content here.
 */

import { sql } from "drizzle-orm";
import {
  pgTable,
  uuid,
  text,
  integer,
  timestamp,
  jsonb,
  uniqueIndex,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
// Task #3603 (403) — FK target for ptw_library_installs.template_id.
import { ptwWorkflowTemplates } from "./ptw";

// ── Type unions ────────────────────────────────────────────────────────

export const PTW_LIBRARY_WORK_TYPES = [
  "hot_work",
  "confined_space",
  "working_at_height",
  "lifting_operations",
  "electrical_loto",
  "excavation",
  "radiography",
  "cold_work",
  "environmental_hazmat",
  "vehicle_mobile_plant",
  "general_maintenance",
  "pressure_testing",
  "pipeline_isolation",
  "tank_entry_cleaning",
  "scaffolding",
  "diving_offshore",
  "explosive_blasting",
  "chemical_handling",
  "simops_coordination",
  "radiation_non_xray",
] as const;
export type PtwLibraryWorkType = (typeof PTW_LIBRARY_WORK_TYPES)[number];

export const PTW_LIBRARY_INDUSTRIES = [
  "oil_gas",
  "petrochemical",
  "construction",
  "manufacturing",
  "mining",
  "marine_offshore",
  "utilities",
  "chemical",
  "aviation",
  "nuclear",
  "general",
] as const;
export type PtwLibraryIndustry = (typeof PTW_LIBRARY_INDUSTRIES)[number];

export const PTW_LIBRARY_REGIONS = [
  "middle_east",
  "north_africa",
  "europe_uk",
  "north_america",
  "asia_pacific",
  "sub_saharan_africa",
  "global",
] as const;
export type PtwLibraryRegion = (typeof PTW_LIBRARY_REGIONS)[number];

export const PTW_LIBRARY_SOURCE_KINDS = ["base", "scenario", "regional"] as const;
export type PtwLibrarySourceKind = (typeof PTW_LIBRARY_SOURCE_KINDS)[number];

export const PTW_LIBRARY_STATUSES = ["draft", "published", "deprecated"] as const;
export type PtwLibraryStatus = (typeof PTW_LIBRARY_STATUSES)[number];

export const PTW_LIBRARY_RISK_TIERS = ["low", "medium", "high", "critical"] as const;
export type PtwLibraryRiskTier = (typeof PTW_LIBRARY_RISK_TIERS)[number];

// ── JSONB field types ──────────────────────────────────────────────────

export type PtwLibraryRegulatoryRef = {
  code: string;
  title: string;
  jurisdiction: string;
  url?: string;
};

export type PtwLibraryJsaEntry = {
  id: string;
  hazard: string;
  risk: string;
  controls: string[];
  residualRisk: "negligible" | "low" | "medium" | "high";
  evidence: string[];
};

export type PtwLibrarySignOffRole = {
  role: string;
  label: string;
  required: boolean;
  notes?: string;
};

export type PtwLibraryHazardPrefill = {
  hazard: string;
  controls: string[];
};

export type PtwLibraryCapacityConsideration = {
  scope: string;
  maxConcurrent: number;
  qualifier?: "per_area" | "per_shift" | "per_tenant";
  rationale: string;
};

export type PtwLibraryCascadeConsideration = {
  kind: string;
  description: string;
};

export type PtwLibraryGateStateOverrides = {
  workerReadiness?: "off" | "advisory" | "blocking";
  equipmentReadiness?: "off" | "advisory" | "blocking";
  areaAccessPolicy?: "off" | "advisory" | "blocking";
  requireSafetySignoff?: boolean;
};

export type PtwLibraryMetadata = {
  version?: number;
  author?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  docPath?: string;
  legacyPresetId?: string;
  legacyRecipeId?: string;
};

// ── Table definition ───────────────────────────────────────────────────

export const ptwLibraryEntries = pgTable(
  "ptw_library_entries",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    slug: text("slug").notNull(),
    sourceKind: text("source_kind").notNull(),
    status: text("status").notNull().default("draft"),
    displayName: text("display_name").notNull(),
    shortDescription: text("short_description").notNull(),
    longDescription: text("long_description").notNull(),
    workType: text("work_type").notNull(),
    industries: text("industries")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    regions: text("regions")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    riskTier: text("risk_tier").notNull(),
    regulatoryRefs: jsonb("regulatory_refs")
      .$type<PtwLibraryRegulatoryRef[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    stepDefinitions: jsonb("step_definitions")
      .$type<Record<string, unknown>[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    jsaEntries: jsonb("jsa_entries")
      .$type<PtwLibraryJsaEntry[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    defaultControls: jsonb("default_controls")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    defaultPpe: jsonb("default_ppe")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    signOffRoles: jsonb("sign_off_roles")
      .$type<PtwLibrarySignOffRole[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    hazardPrefills: jsonb("hazard_prefills")
      .$type<PtwLibraryHazardPrefill[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    controlPrefills: jsonb("control_prefills")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    ppePrefills: jsonb("ppe_prefills")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    capacityConsiderations: jsonb("capacity_considerations")
      .$type<PtwLibraryCapacityConsideration[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    cascadeConsiderations: jsonb("cascade_considerations")
      .$type<PtwLibraryCascadeConsideration[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    gateStateOverrides: jsonb("gate_state_overrides")
      .$type<PtwLibraryGateStateOverrides>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    installCount: integer("install_count").notNull().default(0),
    metadata: jsonb("metadata")
      .$type<PtwLibraryMetadata>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    deprecatedAt: timestamp("deprecated_at", { withTimezone: true }),
  },
  (t) => ({
    idxPtwLibrarySlug: uniqueIndex("idx_ptw_library_entries_slug").on(t.slug),
    idxPtwLibraryStatus: index("idx_ptw_library_entries_status").on(t.status),
    idxPtwLibraryWorkType: index("idx_ptw_library_entries_work_type").on(t.workType),
    idxPtwLibrarySourceKind: index("idx_ptw_library_entries_source_kind").on(t.sourceKind),
  }),
);

// ── Starter list join table ────────────────────────────────────────────
// SA-curated list of up to 8 entries surfaced to new tenants during the
// TenantFirstRunWizard "Set Up Permits" step. When no rows are configured
// the endpoint falls back to the top-N most-installed published entries.

export const ptwLibraryStarterEntries = pgTable(
  "ptw_library_starter_entries",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    slug: text("slug").notNull(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (t) => ({
    idxStarterSlug: uniqueIndex("idx_ptw_library_starter_entries_slug").on(t.slug),
    idxStarterOrder: index("idx_ptw_library_starter_entries_order").on(t.displayOrder),
  }),
);

export type PtwLibraryStarterEntry = typeof ptwLibraryStarterEntries.$inferSelect;

// ── Per-tenant install ledger (Task #2749) ─────────────────────────────
// Records one row per tenant install of a library entry so Super Admins
// can see adoption trends and tenants can see their installation history.
// The install_count column on ptw_library_entries remains the fast-path
// aggregate; this table is the auditable source of truth.

export const ptwLibraryInstalls = pgTable(
  "ptw_library_installs",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    libraryEntrySlug: text("library_entry_slug").notNull(),
    // Task #3464 — FK ON DELETE CASCADE: install records are traceable to
    // their originating library entry; removing the entry removes install
    // history for that entry (acceptable — the installed template copy persists).
    libraryEntryId: uuid("library_entry_id").notNull().references(() => ptwLibraryEntries.id, { onDelete: "cascade" }),
    // Task #3603 (403) — converted uuid→text + FK to ptw_workflow_templates.id
    // (a varchar/text id). Migration 0177 converts the column type and adds the
    // FK. ON DELETE RESTRICT: an installed template with a recorded install-
    // ledger row cannot be hard-deleted out from under the ledger.
    templateId: text("template_id").notNull().references(() => ptwWorkflowTemplates.id, { onDelete: "restrict" }),
    installedBy: text("installed_by"),
    installedAt: timestamp("installed_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (t) => ({
    idxLibInstallsTenant: index("idx_ptw_library_installs_tenant_id").on(t.tenantId),
    idxLibInstallsSlug: index("idx_ptw_library_installs_slug").on(t.libraryEntrySlug),
    idxLibInstallsAt: index("idx_ptw_library_installs_installed_at").on(t.installedAt),
  }),
);

export type PtwLibraryInstall = typeof ptwLibraryInstalls.$inferSelect;
export const insertPtwLibraryInstallSchema = createInsertSchema(ptwLibraryInstalls).omit({
  id: true,
  installedAt: true,
});
export type PtwLibraryInstallInsert = z.infer<typeof insertPtwLibraryInstallSchema>;

// ── Zod insert/select schemas ──────────────────────────────────────────

export const insertPtwLibraryEntrySchema = createInsertSchema(ptwLibraryEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  installCount: true,
});
export type PtwLibraryEntryInsert = z.infer<typeof insertPtwLibraryEntrySchema>;
export type PtwLibraryEntry = typeof ptwLibraryEntries.$inferSelect;

// ── Summary projection used by list endpoints ──────────────────────────

export type PtwLibraryEntrySummary = {
  id: string;
  slug: string;
  sourceKind: PtwLibrarySourceKind;
  status: PtwLibraryStatus;
  displayName: string;
  shortDescription: string;
  workType: PtwLibraryWorkType;
  industries: PtwLibraryIndustry[];
  regions: PtwLibraryRegion[];
  riskTier: PtwLibraryRiskTier;
  regulatoryRefCount: number;
  jsaEntryCount: number;
  installCount: number;
  publishedAt: Date | null;
};
